import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, Clock, Filter, Search, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import Layout from '../components/Layout';
import { useVisibility } from '../hooks/useVisibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DebugDashboard = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [routeFilter, setRouteFilter] = useState('');
  const [sinceMinutes, setSinceMinutes] = useState(60);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const isVisible = useVisibility();

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      params.append('since_minutes', sinceMinutes.toString());
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (routeFilter) {
        params.append('route_contains', routeFilter);
      }

      const response = await axios.get(
        `${API}/debug/logs?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching debug logs:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch debug logs');
      }
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API}/debug/stats?since_minutes=${sinceMinutes}`,
        { headers: getAuthHeaders() }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [statusFilter, routeFilter, sinceMinutes]);

  useEffect(() => {
    if (autoRefresh && isVisible) {
      const interval = setInterval(() => {
        fetchLogs();
        fetchStats();
      }, 10000); // Refresh every 10 seconds
      
      return () => clearInterval(interval);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, isVisible, statusFilter, routeFilter, sinceMinutes]);

  const handleRefresh = () => {
    setLoading(true);
    fetchLogs();
    fetchStats();
  };

  const getStatusBadge = (status) => {
    const variants = {
      'SUCCESS': { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Success' },
      'FAILED': { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Failed' },
      'PARTIAL_SUCCESS': { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Partial' },
      'NEEDS_INPUT': { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Needs Input' },
      'AUTH_ERROR': { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Auth Error' },
      'NOT_FOUND': { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', label: 'Not Found' },
    };
    return variants[status] || variants['SUCCESS'];
  };

  const getLatencyColor = (latency) => {
    if (latency < 500) return 'text-green-400';
    if (latency < 2000) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#050505] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading debug dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#050505] p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Debug Dashboard</h1>
              <p className="text-gray-400">System observability and request logging</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                onClick={() => setAutoRefresh(!autoRefresh)}
                variant={autoRefresh ? "default" : "outline"}
                className="gap-2"
              >
                {autoRefresh ? "Auto-Refresh: ON" : "Auto-Refresh: OFF"}
              </Button>
            </div>
          </div>

          {/* Stats Overview */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="bg-[#0A0A0A] border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                  <div>
                    <div className="text-2xl font-bold text-white">{stats.total_requests}</div>
                    <div className="text-sm text-gray-400">Total Requests</div>
                  </div>
                </div>
              </Card>
              
              <Card className="bg-[#0A0A0A] border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-blue-400" />
                  <div>
                    <div className="text-2xl font-bold text-white">{stats.avg_latency_ms}ms</div>
                    <div className="text-sm text-gray-400">Avg Latency</div>
                  </div>
                </div>
              </Card>
              
              <Card className="bg-[#0A0A0A] border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <AlertCircle className={`w-8 h-8 ${stats.error_rate_percent > 20 ? 'text-red-400' : 'text-yellow-400'}`} />
                  <div>
                    <div className="text-2xl font-bold text-white">{stats.error_rate_percent}%</div>
                    <div className="text-sm text-gray-400">Error Rate</div>
                  </div>
                </div>
              </Card>
              
              <Card className="bg-[#0A0A0A] border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <Filter className="w-8 h-8 text-purple-400" />
                  <div>
                    <div className="text-2xl font-bold text-white">{Object.keys(stats.status_breakdown).length}</div>
                    <div className="text-sm text-gray-400">Status Types</div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="bg-[#0A0A0A] border-gray-800 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-[#050505] border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="SUCCESS">Success</SelectItem>
                    <SelectItem value="FAILED">Failed</SelectItem>
                    <SelectItem value="PARTIAL_SUCCESS">Partial</SelectItem>
                    <SelectItem value="AUTH_ERROR">Auth Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Route Contains</label>
                <Input
                  value={routeFilter}
                  onChange={(e) => setRouteFilter(e.target.value)}
                  placeholder="e.g., /inventions"
                  className="bg-[#050505] border-gray-700 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Time Period</label>
                <Select value={sinceMinutes.toString()} onValueChange={(v) => setSinceMinutes(parseInt(v))}>
                  <SelectTrigger className="bg-[#050505] border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">Last 15 min</SelectItem>
                    <SelectItem value="60">Last hour</SelectItem>
                    <SelectItem value="240">Last 4 hours</SelectItem>
                    <SelectItem value="1440">Last 24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end">
                <Button
                  onClick={() => {
                    setStatusFilter('all');
                    setRouteFilter('');
                    setSinceMinutes(60);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </Card>

          {/* Logs Table */}
          <Card className="bg-[#0A0A0A] border-gray-800">
            <div className="p-6 border-b border-gray-800">
              <h3 className="text-xl font-semibold text-white">Request Logs</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800 text-left text-sm text-gray-400">
                    <th className="p-4">Time</th>
                    <th className="p-4">Method</th>
                    <th className="p-4">Route</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Latency</th>
                    <th className="p-4">Code</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-gray-500">
                        No logs found for the selected filters
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => {
                      const statusBadge = getStatusBadge(log.final_status);
                      return (
                        <tr
                          key={log.log_id || log._id}
                          className="border-b border-gray-800 hover:bg-gray-900/50 cursor-pointer transition-colors"
                          onClick={() => setSelectedLog(log)}
                        >
                          <td className="p-4 text-sm text-gray-300">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </td>
                          <td className="p-4">
                            <Badge variant="outline" className="text-xs">
                              {log.method}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-gray-300 font-mono max-w-md truncate">
                            {log.route}
                          </td>
                          <td className="p-4">
                            <Badge className={`text-xs border ${statusBadge.color}`}>
                              {statusBadge.label}
                            </Badge>
                          </td>
                          <td className={`p-4 text-sm font-mono ${getLatencyColor(log.latency_ms)}`}>
                            {log.latency_ms}ms
                          </td>
                          <td className="p-4 text-sm text-gray-400">
                            {log.status_code}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Selected Log Detail Modal */}
          {selectedLog && (
            <div
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedLog(null)}
            >
              <Card
                className="bg-[#0A0A0A] border-gray-800 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-800 flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Request Details</h3>
                  <Button variant="ghost" onClick={() => setSelectedLog(null)}>✕</Button>
                </div>
                <div className="p-6">
                  <pre className="text-sm text-gray-300 bg-[#050505] p-4 rounded-lg overflow-x-auto">
                    {JSON.stringify(selectedLog, null, 2)}
                  </pre>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DebugDashboard;
